import { useState, useMemo, FC, PropsWithChildren } from "react";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  parseISO,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  isWithinInterval,
  startOfDay,
  endOfDay,
  isSameWeek,
} from "date-fns";
// import { KTIcon, toAbsoluteUrl, WithChildren } from "../../helpers";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Event } from "../types";

type Props = {
  events: Event[];
  setSelectedEvent: any;
  setShowEventModal: any;
};

type ViewMode = "day" | "week" | "month";

export const Calendar: React.FC<Props & PropsWithChildren> = ({
  children,
  events,
  setSelectedEvent,
  setShowEventModal,
}) => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>("month");

  // Navigation functions
  const prevPeriod = () => {
    if (viewMode === "month") setCurrentDate(subMonths(currentDate, 1));
    if (viewMode === "week") setCurrentDate(subWeeks(currentDate, 1));
    if (viewMode === "day") setCurrentDate(addDays(currentDate, -1));
  };

  const nextPeriod = () => {
    if (viewMode === "month") setCurrentDate(addMonths(currentDate, 1));
    if (viewMode === "week") setCurrentDate(addWeeks(currentDate, 1));
    if (viewMode === "day") setCurrentDate(addDays(currentDate, 1));
  };

  const goToToday = () => setCurrentDate(new Date());

  // Date calculations
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const weekStart = startOfWeek(currentDate);
  const weekEnd = endOfWeek(currentDate);
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const getDayEvents = (day:any) => {
    return events.filter((event) => {
      const eventDate = parseISO(event.date);
      const eventEndDate = event.endDate ? parseISO(event.endDate) : eventDate;

      // Check if the day is within the event's date range
      return isWithinInterval(day, {
        start: startOfDay(eventDate),
        end: endOfDay(eventEndDate),
      });
    });
  };

  const getDayColor = (date: any) => {
    const event = events.find((event) => event.date === date);
    return event ? event.dayColor : "bg-default";
  };

  // Time slots for day view
  const timeSlots = useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => {
      const hour = i % 12 || 12;
      const period = i < 12 ? "AM" : "PM";
      return `${hour}:00 ${period}`;
    });
  }, []);

  // Handle event click
  const handleEventClick = (event: Event) => {
    // alert(event.id)
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  function isEventOnDay(event: any, day: any) {
    const eventStart = parseISO(event.date);
    const eventEnd = event.endDate ? parseISO(event.endDate) : eventStart;
    return day >= eventStart && day <= eventEnd;
  }

  const timeToDecimal = (t: any) => {
    if (!t) return 0;
    const [hours, minutes] = t.split(":").map(Number);
    return hours + minutes / 60;
  };

  const getWeekDates = (date: Date) => {
    const start = startOfWeek(date, { weekStartsOn: 0 }); // 0 = Sunday
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  return (
    <>
      <div className="pb-4">
        {/* Navbar */}
        <div className="d-flex align-items-center justify-content-between g-3">
          <div className="col-auto d-flex gap-2 border border-gray-400 rounded p-1">
            <button
              onClick={prevPeriod}
              className="btn btn-sm btn-outline-secondary p-1 px-2"
              title={`Previous ${viewMode}`}
            >
              {/* <KTIcon iconName={"left"} className="fs-1 text-gray-500" /> */}
              Previous
            </button>
            <button
              onClick={goToToday}
              className={`btn btn-sm btn-outline-secondary bg-${
                viewMode === "day" && isSameDay(currentDate, new Date())
                  ? "primary text-gray-100"
                  : viewMode === "week" && isSameWeek(currentDate, new Date())
                  ? "primary text-gray-100"
                  : viewMode === "month" && isSameMonth(currentDate, new Date())
                  ? "primary text-gray-100"
                  : ""
              } p-1 px-2`}
            >
              {viewMode === "day"
                ? "Today"
                : viewMode === "week"
                ? "This Week"
                : "This Month"}
            </button>
            <button
              onClick={nextPeriod}
              className="btn btn-sm btn-outline-secondary p-1 px-2"
              title={`Next ${viewMode}`}
            >
              {/* <KTIcon iconName={"right"} className="fs-1 text-gray-500" />{" "} */}
              Next
            </button>
          </div>

          <div className="col-auto p-0">
            <input
              type="month"
              className="form-control p-1 border border-gray-400 "
              value={format(currentDate, "yyyy-MM")}
              onChange={(e) => {
                if (!e.target.value) return alert("⚠️ Clear is not allowed 🚫");
                const [year, month] = e.target.value.split("-");
                setCurrentDate(
                  new Date(
                    Number(year),
                    Number(month) - 1,
                    new Date().getDate()
                  )
                );
              }}
            />
          </div>

          <div className="col-auto d-flex gap-2 border border-gray-400 rounded p-1">
            <button
              onClick={() => {
                setCurrentDate(
                  new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    new Date().getDate()
                  )
                );

                setViewMode("day");
              }}
              className={`btn btn-sm p-1 px-2 ${
                viewMode === "day" ? "btn-primary" : "btn-outline-secondary"
              }`}
            >
              Day
            </button>
            <button
              onClick={() => setViewMode("week")}
              className={`btn btn-sm p-1 px-2 ${
                viewMode === "week" ? "btn-primary" : "btn-outline-secondary"
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setViewMode("month")}
              className={`btn btn-sm p-1 px-2 ${
                viewMode === "month" ? "btn-primary" : "btn-outline-secondary"
              }`}
            >
              Month
            </button>
          </div>
        </div>
      </div>

      {viewMode === "month" && (
        <div className="d-flex flex-column border border-gray-400 rounded p-1">
          {/* Weekday headers */}
          <div className="row row-cols-7 g-0">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                className="col text-center fw-semibold bg-light py-2 border border-gray-400 rounded"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days - 6 rows to ensure full month display */}
          {Array.from({ length: 6 }).map((_, weekIndex) => (
            <div key={weekIndex} className="row row-cols-7 g-0">
              {Array.from({ length: 7 }).map((_, dayIndex) => {
                const day = addDays(
                  startOfWeek(monthStart),
                  weekIndex * 7 + dayIndex
                );
                const dayEvents = events.filter((event) =>
                  isEventOnDay(event, day)
                );
                const eventsToShow = dayEvents.slice(0, 3); // Show max 3 events
                const hasMoreEvents = dayEvents.length > 3;

                return (
                  <div
                    key={dayIndex}
                    className={`col border border-gray-400 rounded p-2 ${
                      !isSameMonth(day, currentDate) ? "opacity-50" : ""
                    }`}
                    style={{
                      minHeight: "100px",
                      width: "50px",
                      backgroundColor:
                        getDayColor(format(day, "yyyy-MM-dd")) !== "bg-default"
                          ? getDayColor(format(day, "yyyy-MM-dd"))
                          : "transparent",
                    }}
                  >
                    <div className="d-flex justify-content-between">
                      <button
                        onClick={() => {
                          // alert(getDayColor(format(day, "yyyy-MM-dd")));

                          setCurrentDate(
                            new Date(
                              currentDate.getFullYear(),
                              day.getMonth(),
                              Number(format(day, "d"))
                            )
                          );

                          setViewMode("day");
                        }}
                        className={`d-flex fw-bold small cursor-pointer rounded-circle justify-content-center align-items-center ${
                          isSameDay(day, new Date())
                            ? " bg-primary text-white"
                            : " bg-hover-light-danger"
                        }${
                          isSameMonth(day, currentDate)
                            ? " border border-hover-primary"
                            : " border-0"
                        }`}
                        style={{ width: "24px", height: "24px" }}
                        disabled={!isSameMonth(day, currentDate)}
                      >
                        {format(day, "d")}
                      </button>
                    </div>
                    {/* Events : Start */}
                    <div className="mt-2">
                      {eventsToShow.map((event) => (
                        <>
                          {event.type !== "attendance" ? (
                            <div
                              key={`${event.id}-${day.toDateString()}`}
                              onClick={() => handleEventClick(event)}
                              className={`badge d-block text-truncate my-1 text-white cursor-pointer ${event.badgeColor}`}
                              style={{ fontSize: "0.75rem" }}
                            >
                              {event.title}
                            </div>
                          ) : (
                            <div
                              className={`mt-2 cursor-pointer rounded`}
                              onClick={() => handleEventClick(event)}
                            >
                              <div className="row w-100 m-0 gap-1 mb-1">
                                <div
                                  className={`col-sm text-center p-1 fw-bold text-gray-900 fs-7 rounded border border-gray-700`}
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  {event.inTime}
                                </div>
                                <div
                                  className={`col-sm text-center p-1 fw-bold text-gray-900 fs-7 rounded border border-gray-700`}
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  {event.outTime}
                                </div>
                              </div>
                              <div
                                className={`text-center p-1 fw-bold text-gray-900 fs-7 rounded border border-gray-700 mb-1`}
                                style={{ fontSize: "0.7rem" }}
                              >
                                {event.totalHrs}
                              </div>
                              <div
                                className={`text-center p-1 fw-bold text-gray-900 fs-7 rounded border border-gray-700 mb-1`}
                                style={{ fontSize: "0.7rem" }}
                              >
                                {event.description}
                              </div>
                            </div>
                          )}
                        </>
                      ))}

                      {/* Show "..." if there are more events */}
                      {hasMoreEvents && (
                        <div
                          className={`badge d-block text-truncate my-1 text-gray-700 cursor-pointer border border-gray-600`}
                          style={{ fontSize: "0.75rem" }}
                          onClick={() => {
                            setCurrentDate(
                              new Date(
                                currentDate.getFullYear(),
                                currentDate.getMonth(),
                                Number(format(day, "d"))
                              )
                            );
                            setViewMode("day");
                          }}
                        >
                          View More...
                        </div>
                      )}
                    </div>
                    {/* Events : End */}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Week View */}
      {viewMode === "week" && (
        <div className="schedule-view border border-gray-400 rounded p-1 overflow-auto">
          {/* Horizontal Scrollable Week Grid */}
          <div
            className="d-flex border border-gray-400 flex-column"
            style={{ minWidth: "100%" }}
          >
            {/* Header */}
            <div className="d-flex border-bottom border-gray-400 bg-light">
              <div
                className="border-end border-gray-400 text-center fw-semibold py-2"
                style={{ width: "120px" }}
              >
                Time
              </div>
              {getWeekDates(currentDate).map((day, i) => (
                <div
                  key={i}
                  className="border-end border-gray-400 text-center fw-semibold py-2"
                  style={{ minWidth: "120px", flexGrow: 1 }}
                >
                  <div>{format(day, "EEE")}</div>
                  <div
                    className={`small cursor-pointer border border-hover-primary rounded-circle mx-auto ${
                      isSameDay(day, new Date())
                        ? "bg-primary text-white"
                        : "mx-auto"
                    }`}
                    style={{
                      width: "24px",
                      height: "24px",
                      lineHeight: "24px",
                    }}
                    onClick={() => {
                      setCurrentDate(
                        new Date(
                          currentDate.getFullYear(),
                          day.getMonth(),
                          Number(format(day, "d"))
                        )
                      );
                      setViewMode("day");
                    }}
                  >
                    {format(day, "d")}
                  </div>
                </div>
              ))}
            </div>

            {/* Content Grid */}
            <div className="d-flex">
              {/* Time Column */}
              <div
                className="border-end border-gray-400 bg-light"
                style={{ width: "120px" }}
              >
                {timeSlots.map((time, i) => (
                  <div
                    key={i}
                    className="text-end small py-2 pe-2 border-bottom border-gray-400 text-center h-60px"
                  >
                    {time}
                  </div>
                ))}
              </div>

              {/* Event Columns */}
              {getWeekDates(currentDate).map((day, dayIndex) => (
                <div
                  key={dayIndex}
                  className="border-end border-gray-400 position-relative"
                  style={{
                    minWidth: "120px",
                    height: timeSlots.length * 60,
                    flexGrow: 1,
                  }}
                >
                  <div className="position-absolute top-0 start-0 w-100 h-100">
                    {getDayEvents(day).map((event, index) => {
                      // Determine if this is a multi-day event
                      const isMultiDay =
                        event.endDate &&
                        !isSameDay(
                          parseISO(event.date),
                          parseISO(event.endDate)
                        );

                      // Calculate start/end times for this specific day
                      let startTime = "00:00";
                      let endTime = "23:59";

                      // For single-day events or first day of multi-day events
                      if (
                        (!isMultiDay && event.startTime) ||
                        event.inTime ||
                        (isMultiDay &&
                          isSameDay(day, parseISO(event.date)) &&
                          event.startTime) ||
                        event.inTime ||
                        ""
                      ) {
                        startTime = event.startTime || event.inTime || "";
                      }

                      // For single-day events or last day of multi-day events
                      if (
                        (!isMultiDay && event.endTime) ||
                        event.outTime ||
                        (isMultiDay &&
                          isSameDay(
                            day,
                            parseISO(event.endDate || event.outTime || "")
                          ) &&
                          event.endTime) ||
                        event.outTime ||
                        ""
                      ) {
                        endTime = event.endTime || event.outTime || "";
                      }

                      const start = timeToDecimal(startTime);
                      const end = timeToDecimal(endTime);
                      const top = start * 60;
                      const height = (end - start) * 60;

                      const eventCount = getDayEvents(day).length;
                      const segmentWidth = 100 / Math.max(1, eventCount);

                      return (
                        <OverlayTrigger
                          key={`${event.id}-${dayIndex}`}
                          placement="top"
                          delay={{ show: 250, hide: 400 }}
                          overlay={(props) => (
                            <Tooltip {...props}>
                              {isMultiDay
                                ? `${
                                    event.title || event.description
                                  }: ${format(
                                    parseISO(event.date),
                                    "MMM d"
                                  )} - ${format(
                                    parseISO(event.endDate || ""),
                                    "MMM d"
                                  )}`
                                : `${event.title || event.description}: ${
                                    event.startTime || event.inTime || ""
                                  } - ${event.endTime || event.outTime || ""}`}
                            </Tooltip>
                          )}
                        >
                          <div
                            className={`position-absolute cursor-pointer rounded ${
                              event.badgeColor || event.dayColor
                            }`}
                            style={{
                              backgroundColor: getDayColor(
                                format(day, "yyyy-MM-dd")
                              ),
                              top: `${top}px`,
                              height: `${height}px`,
                              left: `${index * segmentWidth}%`,
                              width: `${segmentWidth - 2}%`,
                              boxSizing: "border-box",
                              zIndex: isMultiDay ? 1 : 0,
                            }}
                            onClick={() => handleEventClick(event)}
                          />
                        </OverlayTrigger>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Day View */}
      {viewMode === "day" && (
        <div className="schedule-view border border-gray-400 rounded p-1">
          {/* Header */}
          <div className="row g-0 border-bottom border-gray-400 bg-light">
            <div className="col-2 fw-semibold py-2 border-end border-gray-400 text-center">
              Time
            </div>
            <div className="col-10 fw-semibold py-2 text-center">
              {format(currentDate, "EEEE, MMMM d, yyyy")}
            </div>
          </div>

          <div className="row g-0">
            {/* Time Column */}
            <div className="col-2 border-end border-gray-400 bg-light">
              {timeSlots.map((time, i) => (
                <div
                  key={i}
                  className="text-end small py-2 pe-2 border-bottom border-gray-400 text-center h-60px"
                >
                  {time}
                </div>
              ))}
            </div>

            {/* Events Column */}
            <div
              className="col-10 position-relative"
              style={{ height: timeSlots.length * 60, overflow: "hidden" }}
            >
              {/* Day Events Container */}
              <div
                className="col-10 position-relative"
                style={{
                  height: timeSlots.length * 60,
                  overflow: "hidden",
                  width: "100%",
                }}
              >
                {" "}
                {getDayEvents(currentDate).length === 0 ? (
                  <img
                    alt="No Notifications"
                    src={`media/logos/NoEventFound.png`}
                    className="w-100 theme-light-show"
                  />
                ) : (
                  <div className="position-absolute start-0 top-0 w-100 h-100 px-2">
                    {getDayEvents(currentDate).map((event, index) => {
                      // Determine if this is a multi-day event
                      const isMultiDay =
                        event.endDate &&
                        !isSameDay(
                          parseISO(event.date),
                          parseISO(event.endDate)
                        );

                      // Check if this is the first, middle, or last day of a multi-day event
                      const isFirstDay =
                        isMultiDay &&
                        isSameDay(currentDate, parseISO(event.date));
                      const isLastDay =
                        isMultiDay &&
                        isSameDay(currentDate, parseISO(event.endDate || ""));

                      // Calculate start/end times for this specific day
                      let startTime =
                        event.startTime || event.inTime || "00:00";
                      let endTime = event.endTime || event.outTime || "23:59";

                      // Adjust for first day of multi-day event
                      if (isFirstDay && event.startTime) {
                        startTime = event.startTime || event.inTime || "";
                      }

                      // Adjust for last day of multi-day event
                      if (isLastDay && event.endTime) {
                        endTime = event.endTime || event.outTime || "";
                      }

                      const start = timeToDecimal(startTime);
                      const end = timeToDecimal(endTime);
                      const top = start * 60;
                      const height = (end - start) * 60;

                      // Calculate dynamic width and position
                      const eventCount = getDayEvents(currentDate).length;
                      const segmentWidth = 100 / Math.max(1, eventCount);
                      const left = index * segmentWidth; // 1% gap between events

                      return (
                        <OverlayTrigger
                          key={event.id || index}
                          placement="top"
                          delay={{ show: 250, hide: 400 }}
                          overlay={(props) => (
                            <Tooltip {...props}>
                              {isMultiDay
                                ? `${
                                    event.title || event.description
                                  }\n${format(
                                    parseISO(event.date),
                                    "MMM d"
                                  )} - ${format(
                                    parseISO(event.endDate || ""),
                                    "MMM d"
                                  )}`
                                : `${
                                    event.title || event.description
                                  }\n${startTime} - ${endTime}`}
                              {/* {event.description && `\n${event.description}`} */}
                            </Tooltip>
                          )}
                        >
                          <div
                            className={`position-absolute cursor-pointer rounded ${
                              event.badgeColor || event.dayColor
                            }`}
                            style={{
                              backgroundColor: getDayColor(event.date),
                              top: `${top}px`,
                              height: `${height}px`,
                              left: `${left + 0.5}%`,
                              width: `${segmentWidth - 1}%`,
                              boxSizing: "border-box",
                              zIndex: isMultiDay ? 1 : 0,
                            }}
                            onClick={() => handleEventClick(event)}
                          />
                        </OverlayTrigger>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
